<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DevicesTab from '@/components/equipment/DevicesTab.vue';
import GroupsTab from '@/components/equipment/GroupsTab.vue';
import SitesTab from '@/components/equipment/SitesTab.vue';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import { EquipmentService } from '@/services/equipment.services';
import '@/components/equipment/equipment-ui.css';

const props = defineProps<{ storeId: string }>();

const SECTIONS = [
  { key: 'groups', icon: 'pi-sitemap', label: 'equipment.subgroups' },
  { key: 'devices', icon: 'pi-desktop', label: 'equipment.tabs.devices' },
  { key: 'sites', icon: 'pi-map-marker', label: 'equipment.tabs.sites' },
] as const;
type SectionKey = (typeof SECTIONS)[number]['key'];

const { t } = useI18n({ useScope: 'global' });
const { isSystemAdmin } = useIsSystemAdmin();

const section = ref<SectionKey>('groups');
// Le bouton de création est dans l'en-tête, la fenêtre qu'il ouvre est celle de la rubrique affichée.
const creating = ref(false);
watch(section, () => (creating.value = false));

// Un total par rubrique, lu sur une page d'un seul élément. Le préfixe `equipment` est celui que les créations et
// suppressions invalident : les compteurs suivent sans rien faire de plus.
const searchers = {
  groups: EquipmentService.searchGroups,
  devices: EquipmentService.searchDevices,
  sites: EquipmentService.searchSites,
};
const totalOf = (key: SectionKey) =>
  useQuery({
    queryKey: ['equipment', 'count', key, props.storeId],
    queryFn: async () =>
      (
        await searchers[key]({
          storeIds: [props.storeId],
          page: 1,
          pageSize: 1,
        })
      ).total,
  }).data;
const totals = {
  groups: totalOf('groups'),
  devices: totalOf('devices'),
  sites: totalOf('sites'),
};
</script>

<template>
  <div class="cet">
    <nav class="cet__nav" :aria-label="t('equipment.title')">
      <button
        v-for="item in SECTIONS"
        :key="item.key"
        type="button"
        class="cet__item"
        :class="{ 'cet__item--active': section === item.key }"
        :aria-current="section === item.key"
        @click="section = item.key"
      >
        <i class="pi" :class="item.icon" />
        <span class="cet__label">{{ t(item.label) }}</span>
        <span v-if="totals[item.key].value !== undefined" class="cet__count">
          {{ totals[item.key].value }}
        </span>
      </button>
    </nav>

    <div class="cet__body">
      <div v-if="isSystemAdmin" class="cet__bar">
        <button
          type="button"
          class="eq-btn eq-btn--primary"
          @click="creating = true"
        >
          <i class="pi pi-plus" /> {{ t(`equipment.${section}.create`) }}
        </button>
      </div>
      <GroupsTab
        v-if="section === 'groups'"
        v-model:creating="creating"
        :locked-store-id="storeId"
        cards
      />
      <DevicesTab
        v-else-if="section === 'devices'"
        v-model:creating="creating"
        :locked-store-id="storeId"
        cards
      />
      <SitesTab v-else v-model:creating="creating" :locked-store-id="storeId" />
    </div>
  </div>
</template>

<style scoped>
.cet {
  display: grid;
  grid-template-columns: 14rem minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 899px) {
  .cet {
    grid-template-columns: 1fr;
  }
}
.cet__nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
@media (max-width: 899px) {
  .cet__nav {
    flex-direction: row;
    overflow-x: auto;
  }
}
.cet__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.9rem;
  border: 0;
  border-radius: 0.75rem;
  background: transparent;
  color: var(--nxt-on-surface-muted);
  font-size: 0.9rem;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.cet__item:hover:not(.cet__item--active) {
  color: var(--nxt-on-surface);
}
.cet__item--active {
  background: var(--nxt-surface-high);
  color: var(--nxt-on-surface);
}
.cet__label {
  flex: 1;
}
.cet__count {
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  background: var(--nxt-card-bg);
  font-size: 0.75rem;
}
.cet__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}
.cet__bar {
  display: flex;
  justify-content: flex-end;
}
</style>
